/**
 * Analytics (`/analytics`) — dezelfde twaalf blokken en twee tabellen als de
 * huidige pagina.
 *
 * Endpoints, allemaal met `days` (default 1 = Today) zoals de bundle:
 *   dashboard/conversations · ai-performance · fans · model-performance
 *   dashboard/revenue · sales-by-chatter · takeovers-by-chatter
 */
import { useMemo, useState } from 'react'
import { Avatar, Bar, ErrorNote, Loading, Panel, Seg, Table } from '../components/ui'
import { ModelNaam } from '../components/ModelNaam'
import { ModelFilter, useModelFilter } from '../components/ModelFilter'
import { Donut } from '../components/Donut'
import { HourChart } from '../components/HourChart'
import { useApi } from '../lib/useApi'
import { dt, eur, num, pct } from '../lib/format'
import { AssignSalesDialog } from '../components/AssignSalesDialog'

interface Conversations {
  daily: { date: string; count: number }[]
  per_model: { model_slug: string; conversations: number }[]
  avg_messages_per_conversation: number
  conversations_with_takeover: number
}
interface AiPerf {
  daily: { date: string; avg_confidence: number; responses: number }[]
  model_usage: { model: string; count: number }[]
  confidence_distribution: Record<string, number>
  cost_estimate: { prompt_tokens: number; completion_tokens: number; estimated_usd: number }
}
interface FansData {
  engagement_distribution: Record<string, number>
  new_fans_daily: { date: string; count: number }[]
  language_distribution: Record<string, number>
  platform_distribution: Record<string, number>
}
interface ModelPerf {
  id: number; slug: string; stage_name: string | null
  fancentro_model_id: string | null; f2f_model_id: string | null
  conversations: number; total_followers: number; paying_fans: number
}
interface Takeovers {
  total_takeovers: number
  /** 11-10-2026: echte overnames (claim, andere chatter, eerste antwoord na
   *  6 uur, AI-overdracht); berichten apart, menselijk en massa. */
  per_chatter: { chatter_id: number; name: string; takeovers: number
                 takeovers_by_kind?: Record<string, number>
                 human_messages?: number; mass_messages?: number
                 last_takeover_at: string | null
                 top_models: ({ model_slug: string; count: number } | string)[] | null }[]
}
interface SalesByChatter {
  total_sales: number; method: string
  per_chatter: { chatter_id: number; name: string; sales: number
                 gross: number; net: number; commission_pct: number | null
                 rule: string | null; commission: number | null }[]
}

const PLATFORMS = [
  { value: 'all', label: 'All' },
  { value: 'fancentro', label: 'FanCentro' },
  { value: 'f2f', label: 'F2F' },
]
const RANGES = [
  { value: '1', label: 'Today' }, { value: '7', label: 'Week' },
  { value: '30', label: 'Month' }, { value: '90', label: 'Quarter' },
  { value: '365', label: 'Year' },
]
const CONF_COLORS: Record<string, string> = {
  high: 'var(--acc)', medium: 'var(--acc2)', low: 'var(--dim)',
}
const LANG_COLORS = ['var(--acc)', 'var(--acc2)', 'var(--f2f)', 'var(--fc)', 'var(--dim)']

export default function Analytics() {
  const [platform, setPlatform] = useState('all')
  const [assign, setAssign] = useState(false)
  const [days, setDays] = useState('1')

  // 04-10-2026 (Jordy): filter op één model voor de hele pagina, per pagina.
  const filter = useModelFilter()
  const m = filter.param
  const conv = useApi<Conversations>(`/api/admin/dashboard/conversations?days=${days}${m}`, [days, m])
  const ai = useApi<AiPerf>(`/api/admin/dashboard/ai-performance?days=${days}${m}`, [days, m])
  const fans = useApi<FansData>(`/api/admin/dashboard/fans?days=${days}${m}`, [days, m])
  const perf = useApi<ModelPerf[]>(`/api/admin/dashboard/model-performance?days=${days}${m}`, [days, m])
  const tk = useApi<Takeovers>(`/api/admin/dashboard/takeovers-by-chatter?days=${days}${m}`, [days, m])
  const sales = useApi<SalesByChatter>(`/api/admin/dashboard/sales-by-chatter?days=${days}${m}`, [days, m])

  const models = useMemo(() => {
    // 07-10-2026 (Jordy): verborgen modellen niet in de tegels; alleen
    // Earnings toont ze samen als één regel.
    const rows = (perf.data ?? []).filter((m) => !(m as { hidden_group?: boolean }).hidden_group).filter((m) =>
      platform === 'all' ? true
      : platform === 'fancentro' ? !!m.fancentro_model_id
      : !!m.f2f_model_id)
    return rows.slice().sort((a, b) => b.conversations - a.conversations)
  }, [perf.data, platform])

  const audience = useMemo(() => {
    return models.slice().sort((a, b) => b.total_followers - a.total_followers).slice(0, 10)
  }, [models])

  const conf = ai.data?.confidence_distribution ?? {}
  const eng = fans.data?.engagement_distribution ?? {}
  const lang = fans.data?.language_distribution ?? {}
  const maxConv = models[0]?.conversations || 1
  const maxAud = audience[0]?.total_followers || 1
  const maxEng = Math.max(1, ...Object.values(eng))

  return (
    <>
      <div className="filters">
        <Seg options={PLATFORMS} value={platform} onChange={setPlatform} platform />
        <Seg options={RANGES} value={days} onChange={setDays} />
        <ModelFilter filter={filter} />
        <div className="spacer" style={{ flex: 1 }} />
        <button className="btn ghost sm" type="button"
                onClick={() => setAssign(true)}>Assign Sales</button>
      </div>

      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
        <Panel title="Daily Conversations"
               desc={`avg. ${Number(conv.data?.avg_messages_per_conversation ?? 0).toFixed(1)} messages per chat`}>
          {conv.loading ? <Loading /> : conv.error ? <ErrorNote error={conv.error} /> :
            !conv.data?.daily?.length ? <div className="empty">No conversations in this window</div> : (
            <HourChart data={conv.data.daily as unknown as Record<string, string | number>[]}
                       xKey="date" yKey="count" />
          )}
        </Panel>
        <Panel title="AI Performance"
               desc={`${num(conv.data?.conversations_with_takeover)} chats with takeover`}>
          {!ai.data?.daily?.length ? <div className="empty">No AI activity yet</div> : (
            <HourChart data={ai.data.daily as unknown as Record<string, string | number>[]}
                       xKey="date" yKey="responses" color="var(--acc2)" />
          )}
        </Panel>
        <Panel title="Confidence Distribution">
          <Donut
            title="total"
            segments={Object.entries(conf).map(([k, v]) => ({
              label: k, value: v, color: CONF_COLORS[k] || 'var(--dim)',
            }))}
          />
        </Panel>
      </div>

      <div className="grid" style={{ gridTemplateColumns: '1.3fr 1fr' }}>
        <Panel title="Conversations Per Model">
          {perf.loading ? <Loading /> : (
            <div className="bars">
              {models.slice(0, 10).map((m) => (
                <Bar key={m.id} label={<ModelNaam id={m.id} slug={m.slug} size={18} />} value={m.conversations} max={maxConv}
                     variant={m.fancentro_model_id ? 'fc' : 'f2f'} />
              ))}
            </div>
          )}
        </Panel>
        <Panel title="New Fans Daily">
          {!fans.data?.new_fans_daily?.length ? <div className="empty">No new fans</div> : (
            <HourChart data={fans.data.new_fans_daily as unknown as Record<string, string | number>[]}
                       xKey="date" yKey="count" color="var(--ok)" />
          )}
        </Panel>
      </div>

      <Panel
        title="Audience Per Model"
        desc="Total platform followers and paying subscribers per model — from the latest platform_stats snapshot (~10 min)."
      >
        {perf.loading ? <Loading /> : (
          <div className="bars">
            {audience.map((m) => (
              <Bar key={m.id} label={<ModelNaam id={m.id} slug={m.slug} size={18} />} value={m.total_followers} max={maxAud}
                   variant={m.fancentro_model_id ? 'fc' : 'f2f'} />
            ))}
          </div>
        )}
      </Panel>

      <div className="grid" style={{ gridTemplateColumns: '1fr 1fr 1fr' }}>
        <Panel title="Languages">
          <Donut
            title="fans"
            segments={Object.entries(lang).map(([k, v], i) => ({
              label: k || 'unknown', value: v, color: LANG_COLORS[i % LANG_COLORS.length],
            }))}
          />
        </Panel>
        <Panel title="Engagement">
          <div className="bars">
            {Object.entries(eng).sort((a, b) => b[1] - a[1]).map(([k, v]) => (
              <Bar key={k} label={k} value={v} max={maxEng} />
            ))}
          </div>
        </Panel>
        <Panel title="AI Cost Estimate">
          <div className="mini">
            <div className="f">
              <small>Prompt Tokens</small>
              <b>{num(ai.data?.cost_estimate.prompt_tokens)}</b>
            </div>
            <div className="f">
              <small>Completion Tokens</small>
              <b>{num(ai.data?.cost_estimate.completion_tokens)}</b>
            </div>
          </div>
          <div className="kpi">
            <div className="val" style={{ fontSize: 24 }}>
              ${Number(ai.data?.cost_estimate.estimated_usd ?? 0).toFixed(2)}
            </div>
            <div className="meta">Estimated cost</div>
          </div>
        </Panel>
      </div>

      <Panel
        title="Takeovers per Chatter"
        desc={`Total: ${num(tk.data?.total_takeovers)} takeovers · claim, another chatter, first reply after 6h, AI handoff · messages shown separately`}
        flush
      >
        {tk.loading ? <Loading /> : (
          <Table cols={[
            { key: 'c', label: 'Chatter' }, { key: 't', label: 'Takeovers', num: true },
            { key: 'h', label: 'Messages', num: true }, { key: 'ms', label: 'Mass', num: true },
            { key: 'l', label: 'Last takeover' }, { key: 'm', label: 'Top models' },
          ]} emptyText="No takeovers in this window">
            {(tk.data?.per_chatter ?? []).map((c) => (
              <tr key={c.chatter_id}>
                <td><Avatar name={c.name} />{c.name}</td>
                <td className="num mono"
                    title={Object.entries(c.takeovers_by_kind ?? {}).map(([k, n]) => `${k.replace(/_/g, ' ')}: ${n}`).join(' · ') || undefined}>
                  {num(c.takeovers)}
                </td>
                <td className="num mono">{num(c.human_messages)}</td>
                <td className="num mono mut2">{num(c.mass_messages)}</td>
                <td className="mut2" style={{ fontSize: 12 }}>
                  {c.last_takeover_at ? dt(c.last_takeover_at) : '—'}
                </td>
                <td className="mut" style={{ fontSize: 12 }}>
                  {(c.top_models ?? []).length
                    ? (c.top_models ?? []).map((n, i) => (
                      <span key={i} style={{ marginRight: 8 }}>
                        <ModelNaam slug={typeof n === 'string' ? n : n.model_slug} size={16} />
                      </span>))
                    : '—'}
                </td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>

      <Panel
        title="Sales per Chatter"
        desc={`${num(sales.data?.total_sales)} sales · method ${sales.data?.method ?? '—'}`}
        flush
      >
        {sales.loading ? <Loading /> : (
          <Table cols={[
            { key: 'c', label: 'Chatter' }, { key: 's', label: 'Sales', num: true },
            { key: 'g', label: 'Gross', num: true }, { key: 'n', label: 'Net', num: true },
            { key: 'p', label: '%', num: true }, { key: 'r', label: 'Rule' },
            { key: 'cm', label: 'Commission', num: true },
          ]} emptyText="No sales in this window">
            {(sales.data?.per_chatter ?? []).map((c) => (
              <tr key={c.chatter_id}>
                <td><Avatar name={c.name} />{c.name}</td>
                <td className="num mono">{num(c.sales)}</td>
                <td className="num mono">{eur(c.gross)}</td>
                <td className="num mono">{eur(c.net)}</td>
                <td className="num mono">
                  {c.commission_pct != null ? pct(c.commission_pct) : '—'}
                </td>
                <td className="mut">{c.rule || '—'}</td>
                <td className="num mono">{c.commission != null ? eur(c.commission) : '—'}</td>
              </tr>
            ))}
          </Table>
        )}
      </Panel>

      {assign ? (
        <AssignSalesDialog days={String(days)} platform={platform}
                           onClose={() => setAssign(false)} />
      ) : null}
    </>
  )
}
